/**
 * Real position on the resort plan.
 *
 * This is short on purpose. The hard part of putting a blue dot on a drawing is
 * not the transform, it is having a drawing whose geometry is true — and that
 * was solved upstream, in `scripts/build-resort-plan.mjs`, by drawing the plan
 * *from* coordinates instead of tracing an oblique aerial by eye. Because the
 * generator projected lat/lon into metres to produce the geometry, the function
 * below is that projection's exact inverse. Nothing here is fitted, so there
 * are no residuals to grow as you walk away from a calibration point.
 *
 * The previous plan could not have supported this at any quality. Measured, it
 * was stretched along the shore and squashed inland by roughly half — an aspect
 * of 1.26 against the ground's 0.84 — so a dot would have been right where you
 * calibrated and increasingly wrong everywhere else. Wrong *confidently*, which
 * is worse than absent: guests trust a blue dot more than they trust a map.
 *
 * WHAT THIS IS HONEST ABOUT. Two separate errors stack, and only one of them
 * just got fixed:
 *   - the plan's own error, now a few metres (OSM footprints are volunteer
 *     traces off satellite imagery, not a survey), and
 *   - the phone's, ±5–10 m outdoors and far worse against a building.
 * On a 400 m site that is "you are at the Infinity Lawn", never "you are at
 * table six". It is also useless indoors, which is why the ballroom wants a
 * floor diagram rather than a dot drifting through its walls.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FEATURES,
  ORIGIN,
  M_PER_DEG_LAT,
  M_PER_DEG_LON,
  ROTATE_DEG,
  VIEW,
  type PlanFeature,
} from '../data/resort-plan';

/**
 * The plan's frame is turned `ROTATE_DEG` clockwise off true north/east so the
 * site sits square (see the generator). Every conversion here has to apply the
 * same turn, and this is the one place it could silently go wrong: get the sign
 * backwards and the dot still lands *somewhere on the resort*, plausibly, just
 * rotated about the origin. There is no error, no exception, and nothing on
 * screen that looks broken — which is why the constant is imported from the
 * generated file rather than written out again here.
 */
const ROT = (ROTATE_DEG * Math.PI) / 180;
const ROT_COS = Math.cos(ROT);
const ROT_SIN = Math.sin(ROT);

/** Metres, in the plan's own frame: x north of ORIGIN, y east of ORIGIN. */
export interface PlanPoint {
  x: number;
  y: number;
}

/** Percentages of the view box — what the DOM needs for `left`/`top`. */
export interface PlanPercent {
  x: number;
  y: number;
}

/**
 * Over 400 m the Earth is flat enough that a plate-carrée with a cosine
 * correction is exact to well under the width of the dot. The cosine is not
 * optional though: at 8.7°N a degree of longitude is ~1.2% shorter than one of
 * latitude, which is about 5 m across this site — comparable to the GPS error
 * this is trying to display honestly.
 */
export function planFromLatLon(lat: number, lon: number): PlanPoint {
  const north = (lat - ORIGIN.lat) * M_PER_DEG_LAT;
  const east = (lon - ORIGIN.lon) * M_PER_DEG_LON;
  return {
    x: north * ROT_COS - east * ROT_SIN,
    y: north * ROT_SIN + east * ROT_COS,
  };
}

/** The inverse. Only used by tests and by the `?at=`-style debug helpers, but
 *  having it makes the round trip checkable rather than assumed. */
export function latLonFromPlan(p: PlanPoint): { lat: number; lon: number } {
  const north = p.x * ROT_COS + p.y * ROT_SIN;
  const east = -p.x * ROT_SIN + p.y * ROT_COS;
  return {
    lat: ORIGIN.lat + north / M_PER_DEG_LAT,
    lon: ORIGIN.lon + east / M_PER_DEG_LON,
  };
}

export function percentFromPlan(p: PlanPoint): PlanPercent {
  return {
    x: ((p.x - VIEW.x) / VIEW.w) * 100,
    y: ((p.y - VIEW.y) / VIEW.h) * 100,
  };
}

export function percentFromLatLon(lat: number, lon: number): PlanPercent {
  return percentFromPlan(planFromLatLon(lat, lon));
}

/** A distance in metres as a percentage of the plan's width — for drawing the
 *  accuracy circle at its true size. Plan units *are* metres, which is why this
 *  is a division and not a calibration. */
export function metresToPercent(m: number): number {
  return (m / VIEW.w) * 100;
}

/**
 * Is this fix actually on the resort?
 *
 * Load-bearing rather than defensive. Without it a guest opening the map at
 * Phuket airport — an hour away — gets a dot clamped to the edge of the plan,
 * which reads as "you are at the north-west corner of the resort" rather than
 * "you are not there yet". The margin is generous because the beach and the
 * approach road are legitimately outside the boundary polygon.
 */
export function isOnSite(p: PlanPoint, marginM = 150): boolean {
  return (
    p.x >= VIEW.x - marginM &&
    p.x <= VIEW.x + VIEW.w + marginM &&
    p.y >= VIEW.y - marginM &&
    p.y <= VIEW.y + VIEW.h + marginM
  );
}

/** Percentages of the view box back into plan metres. The picker's half of the
 *  round trip: a tap arrives as a fraction of the stage. */
export function planFromPercent(p: PlanPercent): PlanPoint {
  return { x: VIEW.x + (p.x / 100) * VIEW.w, y: VIEW.y + (p.y / 100) * VIEW.h };
}

function inPolygon({ x, y }: PlanPoint, ring: readonly (readonly [number, number])[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-18) + xi) inside = !inside;
  }
  return inside;
}

/**
 * The building under a point, if any.
 *
 * Exists so that identifying a block is a tap rather than a cross-reference
 * against a printed sheet of OSM way ids. That matters because the ids are the
 * one thing a person has to hand-enter (see data/resort-wings.ts), and asking
 * someone to transcribe `172672606` correctly from a picture is asking for a
 * silent off-by-one-digit error that renders a highlight on the wrong building.
 *
 * Last match wins: features are drawn in ascending z-order, so the one drawn
 * last is the one visibly on top and therefore the one being pointed at.
 */
export function buildingAt(p: PlanPoint): PlanFeature | null {
  let hit: PlanFeature | null = null;
  for (const f of FEATURES) {
    if (!f.closed) continue;
    if (f.kind !== 'block' && f.kind !== 'pale') continue;
    if (inPolygon(p, f.pts)) hit = f;
  }
  return hit;
}

export type LocateState =
  | 'idle'
  | 'asking'
  | 'watching'
  | 'denied'
  | 'unavailable'
  | 'offsite'
  | 'error';

export interface Fix {
  pct: PlanPercent;
  /** The browser's own accuracy estimate, in metres. */
  accuracyM: number;
  /** That radius as a percentage of plan width, ready to draw. */
  accuracyPct: number;
}

/**
 * Watch the guest's position and report it in plan percentages.
 *
 * `watchPosition`, not `getCurrentPosition`: someone walking from their room to
 * the Sangeet wants the dot to keep up, and a single fix taken indoors is
 * usually the worst one they will get all evening.
 */
export function useLiveLocation(): {
  state: LocateState;
  fix: Fix | null;
  start: () => void;
  stop: () => void;
} {
  const [state, setState] = useState<LocateState>('idle');
  const [fix, setFix] = useState<Fix | null>(null);
  const watchId = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (watchId.current !== null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
    setState('idle');
    setFix(null);
  }, []);

  const start = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setState('unavailable');
      return;
    }
    if (watchId.current !== null) return;
    setState('asking');
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        const plan = planFromLatLon(pos.coords.latitude, pos.coords.longitude);
        if (!isOnSite(plan)) {
          setFix(null);
          setState('offsite');
          return;
        }
        const accuracyM = Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : 0;
        setFix({
          pct: percentFromPlan(plan),
          accuracyM,
          accuracyPct: metresToPercent(accuracyM),
        });
        setState('watching');
      },
      (err) => {
        setFix(null);
        setState(err.code === err.PERMISSION_DENIED ? 'denied' : 'error');
      },
      // `maximumAge: 0` because a cached fix from the airport is the exact
      // wrong answer here; the whole point is where you are standing now.
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 },
    );
  }, []);

  // Never leave a GPS watch running behind a closed page — on a phone that is a
  // battery drain the guest cannot see the cause of.
  useEffect(
    () => () => {
      if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
    },
    [],
  );

  return { state, fix, start, stop };
}
